import { VoyageSchedulesModelBase } from './voyage-schedules-model';
import { SchedulesService } from '../services/schedules-service';
import { deepEqual, isOnline } from '../utils/helpers';
import { FetchStatusCodeError } from '../utils/auth-fetch';
import { VoyageDialogPopupManager } from '../../local_modules/voyage-dialog-popup/src/voyage-dialog-popup-manager';
import { ScheduleEntry, IMOsCurrentScheduleState, ScheduleEvents, SimplifiedPortCall, CurrentScheduleState, GetSchedulePromise, ScheduleCacheEntry } from './types/voyage-schedules-model.types';
import { IMO, VoyageImoModel } from './voyage-imo-model';
import { VoyageImoEventNames } from './voyage-imo-model-impl';
import { VesselPlansService } from '../services/vessel-plans-service';
import { IndexedDBService } from '../../indexed-db/services/db_service';

const cacheTimeoutMinutes = 15;
const isVessel = () => (<any>window)["appenv"].vesselIMONumber !== undefined;
const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;

export class VoyageSchedulesModelImpl extends VoyageSchedulesModelBase {
    constructor() {
        super();
        this._imo = VoyageImoModel!.imo;
        this.setLifecycleTimeout(cacheTimeoutMinutes);
        this._loadSchedules();
        this._refreshVesselStates();
        
        let intervalID: ReturnType<typeof setInterval>;
        intervalID = setInterval(() => this._refreshVesselStates(), 15 * 60 * 1000); //refresh every 15 minutes

        let bprIntervalId: ReturnType<typeof setInterval>;
        if(isVesselOnCloud){
            this._cacheBprs();
            bprIntervalId = setInterval(() => this._cacheBprs(), 5 * 60 * 1000); //fetching optimised bpr every 5 minutes
        }

        // Listening for background sync completion
         window.addEventListener('manual-sync-post-data-completed', (event) => {
            const evt = event as CustomEvent;
            console.log("Manual Sync completed : schedule model to refresh the app contents...");
            this._clearSchedules();
            this._loadSchedules();
            this._clearBprs();
            if (intervalID) {
                clearInterval(intervalID); // Stop the existing interval
            }
            intervalID = setInterval(() => this._refreshVesselStates(), 15 * 60 * 1000); 
            if(bprIntervalId){
                clearInterval(bprIntervalId);
            }
            bprIntervalId = setInterval(() => this._cacheBprs(), 5 * 60 * 1000);
        });
        
        VoyageImoModel!.addEventListener(VoyageImoEventNames.ImoUpdated, () => {
            VoyageImoModel?.dispatchEvent(new Event('show-loader'));
            this._imo = VoyageImoModel!.imo;
            this._clearSchedules();
            this._loadSchedules();
            this._clearBprs();
            this._cacheBprs();
            if (intervalID) {
                clearInterval(intervalID); // Stop the existing interval
            }
            intervalID = setInterval(() => this._refreshVesselStates(), 15 * 60 * 1000); 
            if(bprIntervalId){
                clearInterval(bprIntervalId);
            }
            bprIntervalId = setInterval(() => this._cacheBprs(), 5 * 60 * 1000);
        })
    }

    get currentScheduleStates(): IMOsCurrentScheduleState[] {
        const res = [];
        for (let [imoNo, schedule] of this._currentScheduleStates) {
            res.push({
                imoNo,
                schedule
            })
        }
        return res;
    }

    async getScheduleFor(imo: number, includeNumBefore = 0, realtiveFrom = 0): Promise<ScheduleEntry[] | null> {
        if (this._schedulePromises.has(imo)) {
            // Already fetching, wait for it
            await this._schedulePromises.get(imo)!.promise;
        }

        const CACHE_KEY = `${imo}-${realtiveFrom}-${includeNumBefore}`;

        if (this._schedules.has(CACHE_KEY)) {
            if (Date.now() - this._schedules.get(CACHE_KEY)!.timestamp < 60 * 1000) {
                // Use cached version if newer than a minute
                return this._schedules.get(CACHE_KEY)!.schedule;
            }
        }

        const fromDate = new Date();
        if (realtiveFrom) {
            fromDate.setUTCMilliseconds(fromDate.getUTCMilliseconds() - realtiveFrom);
        }

        const promise = this._getScheduleFor(imo, includeNumBefore, fromDate);
        this._schedulePromises.set(imo, { promise });
        const schedule = await promise;
        if (!schedule) {
            this._schedules.delete(CACHE_KEY);
            return null;
        }

        // Emit event if there are changes
        let emitEvent = true;
        if (this._schedules.has(CACHE_KEY) && deepEqual(this._schedules.get(CACHE_KEY)!.schedule, schedule)) {
            emitEvent = false;
        }

        this._schedules.set(CACHE_KEY, { timestamp: Date.now(), schedule });

        if (emitEvent) {
            this.dispatchEvent(new CustomEvent(ScheduleEvents.ScheduleUpdated, { detail: { imo: this._imo } }));
        }
        return schedule;
    }

    getDatesFromSchedule(imoNo: number) {
        const msPerHour = 60 * 60 * 1000;
        const currentTime = new Date();
        let toDate = currentTime;
        let fromDate;
        const state = this._currentScheduleStates.get(imoNo);

        if (state && state.startTime && state.endTime) {
            fromDate = new Date(state.startTime);
            toDate = new Date(Math.min(currentTime.getTime(), state.endTime - msPerHour));
        } else {
            // TBD Fallback: Get tracking 10 days back.
            const period = 10 * 24 * msPerHour;
            fromDate = new Date(currentTime.getTime() - period);
        }

        return { toDate, fromDate, currentTime };
    }

    async changePortCall(portCall: SimplifiedPortCall): Promise<void> {
        if (!isVessel()) {
            throw new Error("changePortCall only available on vessel!");
        }

        try {
            await SchedulesService.changePortCall({
                gsisId: portCall.gsisId,
                id: portCall.localId ? portCall.localId : undefined,
                terminalCode: portCall.terminalCode,
                arrivalAt: portCall.arrivalAt,
                departureAt: portCall.departureAt,
                estimatedpilotboarding: portCall.estimatedpilotboarding,
                estimatedpilotdropoff: portCall.estimatedpilotdropoff
            });
        }
        catch (err) {
            // Show an error dialog and re-throw error
            this._showError(<Error>err);
            throw err;
        }

        await this._refreshVesselStates();

        this._schedules.clear();
        await this.getScheduleFor(this._imo!);
    }

    async addPortCall(portCall: SimplifiedPortCall): Promise<void> {
        if (!isVessel()) {
            throw new Error("addPortCall only available on vessel!");
        }

        try {
            await SchedulesService.addPortCall({
                gsisId: null,
                terminalCode: portCall.terminalCode,
                arrivalAt: portCall.arrivalAt,
                departureAt: portCall.departureAt
            });
        }
        catch (err) {
            // Show an error dialog and re-throw error
            this._showError(<Error>err);
            throw err;
        }

        await this._refreshVesselStates();

        this._schedules.clear();
        await this.getScheduleFor(this._imo!);
    }

    async deletePortCall(portCall: SimplifiedPortCall): Promise<void> {
        if (!isVessel()) {
            throw new Error("deletePortCall only available on vessel!");
        }

        try {
            await SchedulesService.deletePortCall(portCall.gsisId, portCall.localId);
        }
        catch (err) {
            // Show an error dialog and re-throw error
            this._showError(<Error>err);
            throw err;
        }

        await this._refreshVesselStates();

        this._schedules.clear();
        await this.getScheduleFor(this._imo!);
    }

    private _schedules: Map<string, ScheduleCacheEntry> = new Map();
    private _schedulePromises: Map<number, GetSchedulePromise> = new Map();
    private _currentScheduleStates: Map<number, CurrentScheduleState> = new Map();
    private _imo: IMO = null;

    private _clearSchedules() {
        this._schedules.clear();
    }

    private _storeSchedules() {
        const arr = Array.from(this._currentScheduleStates.entries());
        localStorage.setItem('voyage-schedules-model', JSON.stringify(arr));
    }

    private _loadSchedules() {
        try {
            this._currentScheduleStates = new Map(JSON.parse(localStorage.getItem('voyage-schedules-model') || '[]'));

            this.setCacheLifecycle();
            this.dispatchEvent(new Event(ScheduleEvents.ScheduleStatesUpdated));
        } catch (error) {
            this._storeSchedules();
        }
    }

    private async _refreshVesselStates() {
        const schedulesStates = await SchedulesService.fetchScheduleVesselStates();
        if (!schedulesStates) {
            return;
        }

        this._currentScheduleStates.clear();

        schedulesStates.forEach((item:any) => {
            this._currentScheduleStates.set(item.imoNo, item.schedule);
        });

        this._lastFetchedTs = (new Date()).getTime();
        this._storeSchedules();
        this.setCompleteLifecycle();
        this.dispatchEvent(new Event(ScheduleEvents.ScheduleStatesUpdated));
    }

    private async _getScheduleFor(imoNo: number, includeNumBefore = 0, fromDate?: Date): Promise<ScheduleEntry[]> {
        return (await SchedulesService.fetchScheduleDetailsForVessel(imoNo, isVessel() ? 1 : includeNumBefore, fromDate) || {})[imoNo];
    }

    private async _showError(err: Error) {
        let errorDetails = err.message;
        let bodyText = "An error occurred while attempting to update the schedule";

        if (err instanceof FetchStatusCodeError) {
            try {
                const json = await err.response.json();
                if (json.err && json.err.error) {
                    errorDetails = json.err.error;
                }
            }
            catch (e) { }
        }

        if (errorDetails === "arrivalAt time is before departureAt time") {
            bodyText = "Cannot save a port call with arrival time before departure time. Please correct port calls arrival and/or departure time.";
        }

        VoyageDialogPopupManager.createDialog({
            headerText: "Error when updating schedule",
            bodyText: bodyText,
            isError: true,
            errorDetails: errorDetails
        });
    }

    private async _clearBprs(){
        await IndexedDBService.bprs.deleteAll();
    }
    
    private async _cacheBprs(){
        if(!isVesselOnCloud || !isOnline()) {
            console.log("Skipping BPR cache update: not on cloud or offline.");
            return;
        }
        
            try{
                const scheduleEntries = await this._getScheduleFor(VoyageImoModel?.imo!);
    
                if (!scheduleEntries || !scheduleEntries.length) return;
    
                const timeNow = Date.now();
                const endWindow = timeNow + 72 * 60 * 60 * 1000;
    
                //all the schedules in the 72 hour window period
                let schedulesToCache = scheduleEntries.filter(entry => {
                    const etaMs =  entry.etd;
                    return etaMs >= timeNow && etaMs <= endWindow;
                });
                
                if (!schedulesToCache.length) {
                    const states = Array.from(this._currentScheduleStates.values());

                    const currentSchedule = scheduleEntries.find(entry =>
                           entry.departureTerminalCode === states[0].startPort &&
                           entry.arrivalTerminalCode === states[0].endPort
                      );
                    
                      if (!currentSchedule) return;
                      schedulesToCache = [currentSchedule];
                }
    
                await Promise.all(
                    schedulesToCache.map(async entry => {
                      try {
                        const bprs = await VesselPlansService.getOptimizedBPRsByPortPair(
                          entry.departureTerminalCode,
                          entry.arrivalTerminalCode,
                          entry.id
                        );

                        if (bprs != null) {
                          await IndexedDBService.bprs.add({
                            scheduleId: entry.id,
                            data: bprs
                          });
                        } else {
                            await IndexedDBService.bprs.delete(entry.id); //delete outdated bpr for that schedule
                        }
                      } catch (err) {
                        console.error(`Failed to fetch/store BPRs for schedule ${entry.id}:`, err);
                      }
                    })
                  );

                //delete outdated bprs associated with the schedule in the indexed-db
                  const existingBprs = await IndexedDBService.bprs.getAll();
                  const currentScheduleIds = new Set(scheduleEntries.map(entry => entry.id));

                await Promise.all(
                    existingBprs.map(async bprEntry => {
                        if (!currentScheduleIds.has(bprEntry.scheduleId)) {
                            await IndexedDBService.bprs.delete(bprEntry.scheduleId);               
                        }
                    })
                );
            }catch(e){
                console.log(e);
            }
        }
    async getActiveSchedule(imoNo: number): Promise<ScheduleEntry | null> {
        const schedule = await this.getScheduleFor(imoNo);
        if (!schedule) {
            return null;
        }

        const activeSchedule = schedule.find(item => item.active);
        if (!activeSchedule) {
            return null;
        }

        return activeSchedule;
    }
}
